import { BusinessObjectBase } from './base';
import { prescriptionSchema } from '@schemas/business-objects/prescription';
import type { Curriculum } from './curriculum';
import type { Student } from './student';
import type { Id, OralReadingFluency } from '../base-types';

export interface PrescriptionData {
  readonly id: Id;
  readonly active?: boolean;
  readonly oralReadingFluency?: OralReadingFluency;
  readonly studentId: Id;
  readonly student?: Student;
  readonly curriculumId: Id;
  readonly curriculum?: Curriculum;
}

export interface Prescription extends Readonly<PrescriptionData> {}
export class Prescription extends BusinessObjectBase {
  static readonly entityName = 'Prescription' as const;
  static readonly plural = 'prescriptions';
  static schema = prescriptionSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Prescription[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Prescription(data));
  }

  static async findOne(filter?: any): Promise<Prescription | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Prescription(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Prescription> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Prescription(data);
  }

  static async create(
    data: Omit<PrescriptionData, 'id'>,
  ): Promise<Prescription> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Prescription(result);
  }

  static async createMany(
    data: Omit<PrescriptionData, 'id'>[],
  ): Promise<Prescription[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Prescription(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<PrescriptionData>,
  ): Promise<Prescription> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Prescription(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
